import { useState } from "react";
import { Plus } from "lucide-react";
import { Button, EmptyState, Spinner, Icon, CountBadge } from "@cntrl/ui";
import { useCapability } from "../../../app/bandContext.js";
import { useTasks } from "../../../app/hooks/useTasks.js";
import { useTaskPeople } from "../../tasks/taskPeople.js";
import { TaskListRow } from "../../tasks/TaskListRow.js";
import { taskRowsClassName, taskSubLine } from "../../tasks/taskRow.js";
import { TaskDrawer } from "../../tasks/TaskDrawer.js";
import { TaskCreateModal } from "../../tasks/TaskCreateModal.js";
import { SectionRail } from "../sectionRail.js";
import styles from "../section.module.scss";
import own from "./ToDoSection.module.scss";

// To-do (shot-11) — the release checklist REUSING the universal task system: the tasks filtered
// to this release, "+ Add Task" (the shared create modal, release-scoped), and the shared task drawer on a
// row. Everything invalidates through the same task/release keys, so the checklist echoes on the hub +
// overview. Rows the band's release TEMPLATE stamped at creation are ordinary tasks by the time they get
// here — nothing on this surface distinguishes them, by design.
//
// THE ROWS ARE THE APP'S TASK ROW (`TaskListRow`), the SAME idiom as the dashboard's To-do card, the
// Needs-attention triage list and the hub's Release prep panel — the PO reported this checklist as looking
// strange beside every other task list, and it since drifted again into a private 4-column table. Now it is
// the shared row, and its stage reads off the row's status edge like every other task list (ART-343 — the inline
// status pill it used to keep went, so all task rows share one design). Deliberately NOT re-sorted: `artist.tasks` carries `position`/`phase` and a rollout is
// read in the order the template laid it out, so overdue-first (right for the dashboard's triage rail) would
// shuffle a plan out of its own sequence. Urgency rides the row's overdue treatment instead.

export function ToDoSection({ releaseId }: { releaseId: string }) {
  const canWrite = useCapability("releases:write");
  const tasks = useTasks({ releaseId });
  // The shared resolver, not a local seat map: since `core.assignable_people` an ORG staffer can hold an
  // assignment on a band they have no seat on, so a seat list ALONE renders them "Member" (ART-118). The
  // list read carries its own `people` directory for exactly that, and this hook consults both.
  const people = useTaskPeople(tasks.data?.people ?? undefined);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const items = tasks.data?.items ?? [];

  return (
    <div className={styles.section}>
      <SectionRail>
        {/* No "Add preset" here any more: the template is managed in Release settings and stamped at release
            CREATION, so there is no apply-to-this-release action to offer (audit §4.5). */}
        {canWrite ? (
          <Button variant="accent" size="sm" onClick={() => setCreateOpen(true)}>
            <Icon icon={Plus} size={16} /> Add task
          </Button>
        ) : null}
      </SectionRail>

      {tasks.isLoading ? (
        <div className={styles.center}>
          <Spinner size={20} label="Loading checklist…" />
        </div>
      ) : tasks.isError ? (
        <EmptyState
          title="Couldn't load the checklist"
          description={tasks.error instanceof Error && tasks.error.message ? tasks.error.message : "Please try again."}
          action={<Button onClick={() => void tasks.refetch()}>Retry</Button>}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No to-dos yet"
          description="Add a task to get this rollout organized."
          action={canWrite ? <Button onClick={() => setCreateOpen(true)}>Add task</Button> : undefined}
        />
      ) : (
        <div className={`${styles.card} ${own.clip}`}>
          {/* The release builder's card head (the bold card title every other section card uses) with the count as
              the black dot at top right, like the dashboard's To-do card. */}
          <div className={own.head}>
            <h3 className={styles.cardTitle}>To-do</h3>
            <CountBadge count={items.length} />
          </div>
          {/* Full-bleed to the card edges so the overdue wash + crimson rail run edge to edge; the row's own
              inset (`--task-row-pad-inline`) re-adds the card's padding so text still lines up with the count. */}
          <ul className={taskRowsClassName(own.rows)}>
            {items.map((t) => (
              <TaskListRow
                key={t.taskId}
                task={t}
                people={people}
                sub={taskSubLine(t, { release: false })}
                onOpen={() => t.taskId && setOpenTaskId(t.taskId)}
              />
            ))}
          </ul>
        </div>
      )}

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />}
      {createOpen && (
        <TaskCreateModal
          releaseId={releaseId}
          onClose={() => setCreateOpen(false)}
          onCreated={(taskId) => {
            setCreateOpen(false);
            setOpenTaskId(taskId);
          }}
        />
      )}
    </div>
  );
}
